import axios from 'axios';

/**
 * The shared axios instance for all API calls. The base URL comes from the runtime
 * config (window.config.apiUrl), never from a build-time env var, as in spark-board
 * and the OWS Reporting tool.
 */
const http = axios.create({
  baseURL: window.config.apiUrl,
});

/**
 * Identity headers, copied from spark-board.
 * - Legacy mode (no Zitadel issuer): the signed-in staff id, sent as `X-Actor-Id`. It is
 *   seeded from localStorage so a reload stays signed in before React mounts.
 * - OIDC mode: the Zitadel ID token, sent as `Authorization: Bearer`. oidc-client-ts keeps
 *   the durable copy; the identity layer hands us a fresh one at sign-in and on renewal.
 */
const ACTOR_KEY = 'prisma.actorId';
let actorId: string | null = typeof localStorage !== 'undefined' ? localStorage.getItem(ACTOR_KEY) : null;

export const setActorId = (id: string | null) => {
  actorId = id;
  try {
    if (id) localStorage.setItem(ACTOR_KEY, id);
    else localStorage.removeItem(ACTOR_KEY);
  } catch {
    /* ignore storage failures (private mode) */
  }
};

let accessToken: string | null = null;
export const setAccessToken = (t: string | null) => {
  accessToken = t;
};

/**
 * Called on a 401 (expired or invalid token) so the identity layer can sign in again.
 * Only OIDC mode sets it. The guard turns a burst of parallel 401s into one redirect.
 */
let onUnauthorized: (() => void) | null = null;
export const setOnUnauthorized = (fn: (() => void) | null) => {
  onUnauthorized = fn;
};
let handlingUnauthorized = false;

/** Requests on their way, so a reload for a new version waits for a save to finish (AppUpdate). */
let inFlight = 0;
export const requestsInFlight = () => inFlight;
const settled = () => {
  inFlight = Math.max(0, inFlight - 1);
};

http.interceptors.request.use((config) => {
  inFlight++;
  if (accessToken) config.headers.set('Authorization', `Bearer ${accessToken}`);
  if (actorId) config.headers.set('X-Actor-Id', actorId);
  return config;
});

// A 403 (not allowed for your role) is not an auth failure: it passes through so the page can say so.
http.interceptors.response.use(
  (r) => {
    settled();
    return r;
  },
  (error) => {
    settled();
    if (error?.response?.status === 401 && onUnauthorized && !handlingUnauthorized) {
      handlingUnauthorized = true;
      try {
        onUnauthorized();
      } finally {
        window.setTimeout(() => {
          handlingUnauthorized = false;
        }, 2000);
      }
    }
    return Promise.reject(error);
  },
);

export default http;
